import { Stack } from 'component-library';
import { useTranslation } from 'react-i18next';

import { BenchmarkType, PlotReportFactElement } from '@/api/rest/resources/types/fact';
import { ChartHeading } from '@/components/Charts/components/ChartHeading';
import { getDisplayNumber, useDisplayNumber } from '@/hooks/useDisplayNumber';
import { useBenchmarkForFact } from '@/pages/shared/hooks/useBenchmarkForFact';

const buildSegments = (thresholds: number[]): { start: string; end: string; color: string }[] => {
  if (thresholds.length === 0) {
    return [];
  }

  const SEGMENT_COLORS = ['#C6E6C6', '#7CC47C', '#3D9A48', '#1E6B3E', '#004C3D'];
  const FALL_BACK_COLOR = '#003026';

  const segments = thresholds.map((end, i) => {
    const startNumber = i === 0 ? 0 : (thresholds[i - 1] as number);

    const startDisplay = getDisplayNumber(
      startNumber,
      undefined,
      startNumber >= 10 ? { maximumFractionDigits: 1 } : undefined,
    );
    const endDisplay = getDisplayNumber(end, undefined, end >= 10 ? { maximumFractionDigits: 1 } : undefined);

    return {
      start: startDisplay,
      end: endDisplay,
      color: SEGMENT_COLORS[`${i}`] ?? FALL_BACK_COLOR,
    };
  });

  if (segments.at(-1)?.end !== '100') {
    segments.push({
      start: segments.at(-1)?.end ?? '0',
      end: '100',
      color: '#01352a',
    });
  }

  return segments;
};

export const HabitatConnectivityDisplay = ({
  latestAnalysisFact,
  benchmarks,
}: {
  latestAnalysisFact: PlotReportFactElement<number | null>;
  benchmarks: number[];
}) => {
  const { t } = useTranslation();

  const displayValue = useDisplayNumber(
    Number(latestAnalysisFact.value ?? 0),
    ...(latestAnalysisFact.value && latestAnalysisFact.value >= 10 ? [{ maximumFractionDigits: 1 }] : []),
  );
  const segments = buildSegments(benchmarks);

  const benchmark = useBenchmarkForFact(benchmarks, latestAnalysisFact.value, [
    BenchmarkType.very_low,
    BenchmarkType.low,
    BenchmarkType.moderate,
    BenchmarkType.high,
    BenchmarkType.very_high,
  ]);
  const benchmarkIndex = benchmark
    ? [
        BenchmarkType.very_low,
        BenchmarkType.low,
        BenchmarkType.moderate,
        BenchmarkType.high,
        BenchmarkType.very_high,
      ].indexOf(benchmark)
    : null;
  const markerPosition = Math.min(Math.max(Number(latestAnalysisFact.value ?? 0), 0), 100);

  return (
    <Stack spacing={4} className='w-full'>
      <Stack spacing={2}>
        <ChartHeading
          heading={t('shared.ncaDetail.details.habitatConnectivity.labels.coreHabitatRadius')}
          popover={{
            title: t('shared.ncaDetail.details.habitatConnectivity.labels.coreHabitatRadius'),
            body: t('shared.ncaDetail.details.habitatConnectivity.labels.coreHabitatRadiusTooltip'),
          }}
          className='w-full px-6 sm:px-0'
        />
        <span className='typography-display1 font-bold text-primary-100'>{displayValue}%</span>
      </Stack>
      {segments.length > 0 && (
        <Stack spacing={1}>
          <span className='typography-overline text-text-secondary'>
            {t('shared.ncaDetail.details.habitatConnectivity.labels.connectivityScale')}
          </span>
          <Stack className='w-full' data-testid='connectivity-bar'>
            <Stack spacing={1}>
              <Stack direction='row' className='h-6 overflow-hidden rounded-2xl border border-divider'>
                {segments.map((seg, i) => (
                  <div
                    key={i}
                    style={{
                      width: `${parseFloat(seg.end) - parseFloat(seg.start)}%`,
                      backgroundColor: seg.color,
                    }}
                  />
                ))}
              </Stack>
              <div className='relative w-full'>
                <Stack direction='row' className='w-full'>
                  {segments.map((seg, i) => (
                    <div
                      key={i}
                      className='justify-center typography-caption text-text-secondary text-center'
                      style={{ width: `${parseFloat(seg.end) - parseFloat(seg.start)}%` }}
                    >
                      {parseFloat(seg.start) === 0 ? `<${seg.end}%` : `${seg.start}-${seg.end}%`}
                    </div>
                  ))}
                </Stack>
                {benchmarkIndex !== null && (
                  <i
                    className='ri-triangle-fill absolute top-full -translate-x-1/2 text-sm text-primary-100'
                    style={{ left: `${markerPosition}%` }}
                  />
                )}
              </div>
            </Stack>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};
